<template>
  <DxPopup
    :visible="visible"
    :show-title="true"
    :show-close-button="true"
    :drag-enabled="false"
    :hide-on-outside-click="false"
    :title="popupTitle"
    width="min(1000px, 96vw)"
    height="min(720px, 92vh)"
    @hiding="onClose"
  >
    <div class="work-interval-price-popup">
      <GridPanelHeader
        :title="''"
        :grid-ref="$refs.priceGrid"
        :show-add="true"
        :show-filter="false"
        :show-export="false"
        :show-columns="false"
        :no-bottom-margin="true"
        @add="addPriceRow"
      />
      <div v-if="conflictRowKeys.length" class="work-interval-price-popup__validation-message">
        {{ $t("stock_management.products.interval_prices.overlapping_intervals") }}
      </div>
      <DxDataGrid
        ref="priceGrid"
        :data-source="priceDataSource"
        :remote-operations="false"
        :show-borders="true"
        :show-column-lines="true"
        :row-alternation-enabled="true"
        :column-auto-width="false"
        :element-attr="{ class: 'smagria-grid work-interval-price-grid' }"
        @init-new-row="onInitNewRow"
        @row-prepared="onRowPrepared"
        @row-updating="clearConflictRows"
      >
        <DxColumn
          data-field="date_from"
          data-type="date"
          :format="getDxGridDefaultDateFormat"
          :caption="$t('common.date_from')"
          :allow-filtering="true"
          :filter-operations="['=', '<', '<=', '>', '>=']"
        >
          <DxRequiredRule :message="$t('validation.field_is_required')" />
        </DxColumn>
        <DxColumn
          data-field="date_to"
          data-type="date"
          :format="getDxGridDefaultDateFormat"
          :caption="$t('common.date_to')"
          :allow-filtering="true"
          :filter-operations="['=', '<', '<=', '>', '>=']"
        >
          <DxRequiredRule :message="$t('validation.field_is_required')" />
        </DxColumn>
        <DxColumn
          data-field="unit_price"
          data-type="number"
          :format="getDxGridDefaultNumberFormat"
          :caption="$t('common.unit_price')"
          :allow-filtering="true"
          :filter-operations="['=', '<', '<=', '>', '>=']"
        >
          <DxRequiredRule :message="$t('validation.field_is_required')" />
        </DxColumn>
        <DxColumn
          data-field="department_uuids"
          :caption="$t('stock_management.products.interval_prices.departments')"
          cell-template="departmentCellTemplate"
          edit-cell-template="departmentEditTemplate"
          :calculate-filter-expression="calculateDepartmentFilterExpression"
          :allow-filtering="true"
          :filter-operations="['contains']"
          :min-width="280"
        />
        <template #departmentCellTemplate="{ data }">
          <span>{{ getDepartmentNames(data.data) }}</span>
        </template>
        <template #departmentEditTemplate="{ data }">
          <DxTagBox
            :value="data.value || []"
            :data-source="departments"
            value-expr="uuid"
            display-expr="name"
            :search-enabled="true"
            :show-selection-controls="true"
            :show-clear-button="true"
            apply-value-mode="instantly"
            @value-changed="data.setValue($event.value)"
          />
        </template>
        <DxEditing mode="form" :allow-adding="false" :allow-updating="true" :allow-deleting="true" :use-icons="true">
          <DxTexts
            :edit-row="$t('common.edit')"
            :delete-row="$t('common.delete')"
            :save-row-changes="$t('common.save')"
            :cancel-row-changes="$t('common.cancel')"
            :confirm-delete-title="$t('common.confirm_delete_title')"
            :confirm-delete-message="$t('common.confirm_delete', { title: $t('common.unit_price') })"
          />
        </DxEditing>
        <DxLoadPanel :enabled="true" :show-indicator="true" :show-pane="true" />
        <DxFilterRow :visible="true" apply-filter="auto" />
        <DxHeaderFilter :visible="false" />
        <DxPaging :enabled="false" />
        <DxScrolling mode="standard" show-scrollbar="always" />
      </DxDataGrid>

      <div class="work-interval-price-popup__actions">
        <SmagriaButton
          :label="$t('common.save')"
          :disabled="loading"
          :action="onSave"
          :customClass="'primary'"
          :width="'150px'"
        />
        <SmagriaButton
          :label="$t('common.cancel')"
          :disabled="loading"
          :action="onClose"
          :customClass="'secondary border margin-left'"
          :width="'130px'"
        />
      </div>
    </div>
  </DxPopup>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxPopup from "devextreme-vue/popup";
import DxTagBox from "devextreme-vue/tag-box";
import DxDataGrid, {
  DxColumn,
  DxEditing,
  DxFilterRow,
  DxHeaderFilter,
  DxLoadPanel,
  DxPaging,
  DxRequiredRule,
  DxScrolling,
  DxTexts,
} from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { format } from "date-fns";
import { errorMessage, successMessage } from "@/helpers/common";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";

export default {
  name: "ProductDateIntervalUnitPricePopup",
  components: {
    DxPopup,
    DxTagBox,
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxFilterRow,
    DxHeaderFilter,
    DxLoadPanel,
    DxPaging,
    DxRequiredRule,
    DxScrolling,
    DxTexts,
    GridPanelHeader,
    SmagriaButton,
  },
  props: {
    visible: { type: Boolean, default: false },
    product: { type: Object, required: true },
  },
  data() {
    return {
      rows: [],
      loading: false,
      nextLocalKey: 1,
      pricesLoaded: false,
      conflictRowKeys: [],
      priceDataSource: new DataSource({
        store: new CustomStore({
          key: "local_key",
          load: () => this.loadData(),
          insert: (values) => this.insertPrice(values),
          update: (key, values) => this.updatePrice(key, values),
          remove: (key) => this.removePrice(key),
        }),
      }),
    };
  },
  computed: {
    ...mapGetters({
      departments: "company/departments",
      company: "auth/domain",
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
      getDxGridDefaultNumberFormat: "settings/getDxGridDefaultNumberFormat",
    }),
    popupTitle() {
      return `${this.$t("stock_management.products.interval_prices.title")}: ${this.product.name}`;
    },
  },
  methods: {
    ...mapActions({
      fetchPrices: "smProducts/getProductDateIntervalUnitPrices",
      syncPrices: "smProducts/syncProductDateIntervalUnitPrices",
      getDepartments: "company/getDepartments",
    }),
    async loadData() {
      if (this.pricesLoaded) return this.rows;

      this.loading = true;
      try {
        if (!this.departments.length && this.company?.uuid) {
          await this.getDepartments({ company_uuid: this.company.uuid });
        }
        const response = await this.fetchPrices({ product_uuid: this.product.uuid });
        this.rows = (response || []).map((row) => ({
          ...row,
          date_from: row.date_from ? new Date(`${row.date_from}T00:00:00`) : null,
          date_to: row.date_to ? new Date(`${row.date_to}T00:00:00`) : null,
          local_key: row.uuid || `new-${this.nextLocalKey++}`,
        }));
        this.pricesLoaded = true;
        return this.rows;
      } catch (error) {
        errorMessage(this, error?.message || this.$t("stock_management.products.interval_prices.load_error"));
        this.pricesLoaded = true;
        return [];
      } finally {
        this.loading = false;
      }
    },
    onInitNewRow(e) {
      e.data.local_key = `new-${this.nextLocalKey++}`;
      e.data.uuid = null;
      e.data.department_uuids = [];
    },
    addPriceRow() {
      this.$refs.priceGrid?.instance?.addRow();
    },
    insertPrice(values) {
      this.clearConflictRows();
      const row = {
        ...values,
        local_key: values.local_key || `new-${this.nextLocalKey++}`,
        department_uuids: values.department_uuids || [],
      };
      this.rows.push(row);
      return row;
    },
    updatePrice(key, values) {
      this.clearConflictRows();
      const row = this.rows.find((item) => item.local_key === key);
      if (row) Object.assign(row, values);
      return key;
    },
    removePrice(key) {
      this.clearConflictRows();
      const index = this.rows.findIndex((item) => item.local_key === key);
      if (index >= 0) this.rows.splice(index, 1);
      return key;
    },
    getDepartmentNames(row) {
      const selected = new Set(row.department_uuids || []);
      if (!selected.size) return this.$t("stock_management.products.interval_prices.all_departments");
      return this.departments.filter((department) => selected.has(department.uuid)).map((department) => department.name).join(", ");
    },
    calculateDepartmentFilterExpression(filterValue) {
      const search = String(filterValue || "").toLowerCase();
      return (row) => this.getDepartmentNames(row).toLowerCase().includes(search);
    },
    onRowPrepared(e) {
      if (e.rowType !== "data") return;
      e.rowElement.classList.toggle("work-interval-price-row--error", this.conflictRowKeys.includes(e.data.local_key));
    },
    clearConflictRows() {
      if (!this.conflictRowKeys.length) return;
      this.conflictRowKeys = [];
      this.$nextTick(() => this.$refs.priceGrid?.instance?.repaint());
    },
    setConflictRows(keys) {
      this.conflictRowKeys = Array.from(new Set(keys));
      this.$nextTick(() => this.$refs.priceGrid?.instance?.repaint());
    },
    async onSave() {
      const grid = this.$refs.priceGrid?.instance;
      if (grid?.hasEditData()) {
        await grid.saveEditData();
        if (grid.hasEditData()) return;
      }

      const validationError = this.validateRows();
      if (validationError) {
        errorMessage(this, validationError);
        return;
      }

      this.loading = true;
      try {
        await this.syncPrices({
          product_uuid: this.product.uuid,
          items: this.rows.map((row) => ({
            uuid: row.uuid || null,
            date_from: this.toDateString(row.date_from),
            date_to: this.toDateString(row.date_to),
            unit_price: Number(row.unit_price),
            department_uuids: row.department_uuids || [],
          })),
        });
        successMessage(this, this.$t("stock_management.products.interval_prices.save_success"));
        this.$emit("save");
      } catch (error) {
        errorMessage(this, error?.message || this.$t("stock_management.products.interval_prices.save_error"));
      } finally {
        this.loading = false;
      }
    },
    validateRows() {
      this.setConflictRows([]);
      for (const row of this.rows) {
        if (!row.date_from || !row.date_to || row.unit_price === null || row.unit_price === undefined || Number(row.unit_price) < 0) {
          return this.$t("stock_management.products.interval_prices.complete_rows");
        }
        if (this.toDateString(row.date_to) < this.toDateString(row.date_from)) {
          return this.$t("stock_management.products.interval_prices.invalid_interval");
        }
      }

      const conflictingKeys = [];
      for (let index = 0; index < this.rows.length; index++) {
        for (let otherIndex = index + 1; otherIndex < this.rows.length; otherIndex++) {
          const row = this.rows[index];
          const other = this.rows[otherIndex];
          const rowDepartments = row.department_uuids || [];
          const otherDepartments = other.department_uuids || [];
          const sameScope = !rowDepartments.length
            || !otherDepartments.length
            || rowDepartments.some((uuid) => otherDepartments.includes(uuid));
          const overlaps = this.toDateString(row.date_from) <= this.toDateString(other.date_to)
            && this.toDateString(other.date_from) <= this.toDateString(row.date_to);
          if (sameScope && overlaps) conflictingKeys.push(row.local_key, other.local_key);
        }
      }

      if (conflictingKeys.length) {
        this.setConflictRows(conflictingKeys);
        return this.$t("stock_management.products.interval_prices.overlapping_intervals");
      }
      return null;
    },
    toDateString(value) {
      return value ? format(value instanceof Date ? value : new Date(value), "yyyy-MM-dd") : null;
    },
    onClose() {
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/components/workDateIntervalUnitPrice.scss";
</style>
